import { TFunction } from "i18next";

import { IOption } from "../components/SelectDropdown";

/**
 * Which Surveys option list a key belongs to. One prefix per API enum (or portal constant).
 */
export type SurveyOptionList =
  | "surveyType"
  | "scoreStrategy"
  | "questionType"
  | "displayRule"
  | "dataSource";

/**
 * The label for a system-defined Surveys option, in the user's language.
 *
 * EN-5285: the survey builder showed these as the API's English names, or as the raw enum
 * value, so they stayed English in every language. The key is built from the option's value
 * (the enum it saves), never from its English name, so translating a label cannot change what
 * is stored.
 *
 * `fallback` (the API's name, when it sends one) is used for a value the portal has no key
 * for yet, and the value itself when there is no name, so a new option never reads as a raw
 * key.
 */
export function getSurveyOptionLabel(
  list: SurveyOptionList,
  value: string,
  t: TFunction,
  fallback?: string
): string {
  const key = value.charAt(0).toLowerCase() + value.slice(1);

  return t(`surveys_options_${list}_${key}`, {
    defaultValue: fallback ?? value,
  });
}

/**
 * The same options with their labels in the user's language. Each option's `label` (the API's
 * name, or the raw value) is the fallback; `value` and `key` are kept as they are.
 *
 * Call it at render, not when the options are fetched: the published translations can arrive
 * after the fetch, and a label stored in state would stay in English.
 */
export function getTranslatedSurveyOptions(
  list: SurveyOptionList,
  options: IOption[],
  t: TFunction
): IOption[] {
  return options.map((option) => ({
    ...option,
    label: getSurveyOptionLabel(list, String(option.value), t, option.label),
  }));
}
